import { useEffect, useState } from "react";
import { previewManufacturingNamedVariantApplication } from "@/lib/manufacturing/namedVariantsClient";
import {
  formatImpactQty,
  summarizeNamedVariantPreviewImpact,
  type NamedVariantPreviewImpact,
} from "@/lib/manufacturing/namedVariantPreviewImpact";

/** Green before→after impact strip for an expanded application (loads preview BOM). */
export function ManufacturingNamedVariantApplicationImpact({
  applicationId,
  effectKey,
}: {
  applicationId: string;
  /** Re-fetch when materials_effect changes (e.g. after save). */
  effectKey: string;
}) {
  const [impact, setImpact] = useState<NamedVariantPreviewImpact | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    setError(null);
    void previewManufacturingNamedVariantApplication(applicationId).then((r) => {
      if (cancelled) return;
      setBusy(false);
      if (!r.ok) {
        setError(r.error);
        setImpact(null);
        setWarnings([]);
        return;
      }
      setImpact(summarizeNamedVariantPreviewImpact(r.data.lines));
      setWarnings(r.data.warnings ?? []);
    });
    return () => {
      cancelled = true;
    };
  }, [applicationId, effectKey]);

  if (busy && !impact) {
    return (
      <div className="text-xs text-emerald-700/70 dark:text-emerald-300/70">
        Computing materials impact…
      </div>
    );
  }
  if (error) {
    return <div className="text-xs text-amber-700 dark:text-amber-300">{error}</div>;
  }
  if (!impact || (impact.materials_affected === 0 && impact.slots.length === 0)) {
    return (
      <div className="space-y-1">
        <div className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
          No materials change
        </div>
        {warnings.length > 0 ? (
          <ul className="list-disc space-y-0.5 pl-4 text-[11px] text-amber-800 dark:text-amber-200">
            {warnings.slice(0, 4).map((w) => (
              <li key={w}>{w}</li>
            ))}
            {warnings.length > 4 ? <li>+{warnings.length - 4} more…</li> : null}
          </ul>
        ) : null}
      </div>
    );
  }

  const qtyLabel =
    impact.qty_added === 0
      ? "qty unchanged"
      : `${impact.qty_added > 0 ? "+" : ""}${formatImpactQty(impact.qty_added)} qty`;
  const costLabel =
    Math.abs(impact.cost_delta) < 0.005
      ? null
      : `${impact.cost_delta > 0 ? "+" : ""}$${impact.cost_delta.toFixed(2)} COGS`;

  return (
    <div className="space-y-1 border-t border-emerald-200/80 pt-2 dark:border-emerald-800/60">
      {impact.slots.map((s) => {
        const mat =
          s.material_codes.length === 1
            ? s.material_codes[0]
            : s.material_codes.length > 1
              ? `${s.material_codes.length} materials`
              : "";
        const delta =
          s.add_qty === 0
            ? "swap"
            : `${s.add_qty > 0 ? "+" : ""}${formatImpactQty(s.add_qty)}`;
        return (
          <div
            key={s.slot_label}
            className="text-xs font-medium text-emerald-700 dark:text-emerald-300"
          >
            <span className="font-semibold">{s.slot_label}</span>
            {": "}
            {formatImpactQty(s.before_qty)} → {formatImpactQty(s.after_qty)}
            {s.add_qty !== 0 ? ` (${delta})` : " (material swap)"}
            {mat ? ` · ${mat}` : ""}
          </div>
        );
      })}
      <div className="text-xs font-semibold text-emerald-800 dark:text-emerald-200">
        {qtyLabel}
        {" · "}
        {impact.materials_affected} material
        {impact.materials_affected === 1 ? "" : "s"} affected
        {costLabel ? ` · ${costLabel}` : ""}
      </div>
    </div>
  );
}
